/**
 * Types pour la gestion des erreurs d'abonnement et de quota
 * Conforme à la spécification backend
 * 
 * @see chat.md - Section Gestion des erreurs
 */

/**
 * Types d'erreurs de streaming liées aux abonnements
 */
export type SubscriptionErrorType = 
  | 'quota_exhausted'       // Quota de requêtes épuisé
  | 'subscription_expired'  // Abonnement expiré
  | 'subscription_inactive' // Abonnement inactif
  | 'plan_limit_reached'    // Limite du plan atteinte
  | 'payment_required';     // Paiement requis

/**
 * Métadonnées d'erreur d'abonnement retournées par le backend
 */
export interface SubscriptionErrorMetadata {
  /** Type d'erreur spécifique */
  error_type: SubscriptionErrorType;
  
  /** URL pour renouveler l'abonnement (si applicable) */
  subscription_renewal_url?: string;
  
  /** Indique si une action utilisateur est requise */
  requires_action: boolean;
  
  /** Date d'expiration de l'abonnement (si applicable) */
  expiration_date?: string;
  
  /** Quota restant (0 si épuisé) */
  remaining_quota?: number;
  
  /** Quota total du plan */
  total_quota?: number;
  
  /** Date de réinitialisation du quota (si applicable) */
  quota_reset_date?: string;
  
  /** Nom du plan actuel */
  current_plan?: string;
  
  /** Plan suggéré pour upgrade */
  suggested_plan?: string;
}

/**
 * Erreur enrichie avec métadonnées d'abonnement
 */
export interface EnrichedStreamError {
  /** Message d'erreur lisible */
  message: string;
  
  /** Type d'erreur de streaming */
  streamType: 'error';
  
  /** Métadonnées d'abonnement (si erreur liée à l'abonnement) */
  subscriptionError?: SubscriptionErrorMetadata;
  
  /** Indique si c'est une erreur d'abonnement/quota */
  isSubscriptionError: boolean;
  
  /** Métadonnées brutes */
  rawMetadata?: Record<string, unknown>;
}

/**
 * État d'abonnement de l'utilisateur
 */
export interface SubscriptionState {
  /** Indique si l'utilisateur a un abonnement actif */
  isActive: boolean;
  
  /** Nom du plan actuel */
  planName: string;
  
  /** Date d'expiration */
  expirationDate?: string;
  
  /** Quota de requêtes restant */
  remainingQuota: number;
  
  /** Quota total du plan */
  totalQuota: number;
  
  /** Date de réinitialisation du quota */
  quotaResetDate?: string;
}

/**
 * Vérifie si une erreur est liée à l'abonnement/quota
 */
export function isSubscriptionError(metadata?: Record<string, unknown>): metadata is SubscriptionErrorMetadata {
  if (!metadata) return false;
  
  const errorType = metadata.error_type as string | undefined;
  const validTypes: SubscriptionErrorType[] = [
    'quota_exhausted',
    'subscription_expired',
    'subscription_inactive',
    'plan_limit_reached',
    'payment_required'
  ];
  
  return !!errorType && validTypes.includes(errorType as SubscriptionErrorType);
}

/**
 * Parse les métadonnées d'erreur et extrait les informations d'abonnement
 */
export function parseSubscriptionError(
  content: string,
  metadata?: Record<string, unknown>
): EnrichedStreamError {
  if (isSubscriptionError(metadata)) {
    return {
      message: content,
      streamType: 'error',
      isSubscriptionError: true,
      subscriptionError: metadata as SubscriptionErrorMetadata,
      rawMetadata: metadata
    };
  }
  
  return {
    message: content,
    streamType: 'error',
    isSubscriptionError: false,
    rawMetadata: metadata
  };
}

/**
 * Messages d'erreur localisés par type
 */
export const SUBSCRIPTION_ERROR_MESSAGES: Record<SubscriptionErrorType, { title: string; description: string }> = {
  quota_exhausted: {
    title: 'Quota épuisé',
    description: 'Vous avez atteint la limite de requêtes pour la période actuelle.'
  },
  subscription_expired: {
    title: 'Abonnement expiré',
    description: 'Votre abonnement a expiré. Veuillez le renouveler pour continuer.'
  },
  subscription_inactive: {
    title: 'Abonnement inactif',
    description: 'Votre abonnement n\'est pas actif. Veuillez contacter le support.'
  },
  plan_limit_reached: {
    title: 'Limite du plan atteinte',
    description: 'Vous avez atteint la limite de votre plan actuel. Passez à un plan supérieur pour continuer.'
  },
  payment_required: {
    title: 'Paiement requis',
    description: 'Un paiement est requis pour continuer à utiliser ce service.'
  }
};

/**
 * Obtient le message localisé pour un type d'erreur
 */
export function getSubscriptionErrorMessage(errorType: SubscriptionErrorType): { title: string; description: string } {
  return SUBSCRIPTION_ERROR_MESSAGES[errorType] || {
    title: 'Erreur d\'abonnement',
    description: 'Une erreur liée à votre abonnement s\'est produite.'
  };
}
